import { lazy, Suspense } from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { HelmetProvider, Helmet } from "react-helmet-async";
import { DocumentMeta } from "./hooks/useDocumentMeta";
import { ScrollToHash } from "./utils/ScrollToHash";
import ScrollToTop from "./utils/ScrollToTop";

const LandingPage   = lazy(() => import("./pages/LandingPage"));
const AboutUs       = lazy(() => import("./pages/AboutUs"));
const ChristomexWay = lazy(() => import("./pages/ChristomexWay"));
const NotFoundPage  = lazy(() => import("./pages/NotFoundPage"));
const Careers       = lazy(() => import("./pages/Careers"));
const SharedValues  = lazy(() => import("./pages/SharedValues"));
const Sustainability = lazy(() => import("./pages/Sustainability"));
const ContactUs     = lazy(() => import("./pages/ContactUs"));
const Products      = lazy(() => import("./pages/Products"));

const BASE_URL = "https://christomexwasterecycling.com";
const OG_IMAGE =
  "https://res.cloudinary.com/dklswdjev/image/upload/v1777035932/community_fortune_placeholder_lhjqlh.png";

const JSON_LD = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "Christomex",
  url: BASE_URL,
  logo: `${BASE_URL}/logo.png`,
  sameAs: [
    "https://www.facebook.com/ChristomexWasteRecycler",
    "https://www.instagram.com/christomex_recycling",
  ],
  contactPoint: {
    "@type": "ContactPoint",
    contactType: "customer service",
    url: `${BASE_URL}/contact`,
  },
};

function App() {
  return (
    <HelmetProvider>
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(JSON_LD)}</script>
      </Helmet>

      <Router>
        <div className="m-0 p-0">
          <ScrollToTop />
          <ScrollToHash />
          <Suspense fallback={null}>
            <Routes>
              <Route
                path="/"
                element={
                  <>
                    <DocumentMeta
                      title="Christomex"
                      description="Christomex — Building sustainable solutions to create a cleaner tomorrow through innovative recycling technologies"
                      url={BASE_URL}
                      image={OG_IMAGE}
                    />
                    <LandingPage />
                  </>
                }
              />
              <Route
                path="/about-us"
                element={
                  <>
                    <DocumentMeta
                      title="About Us"
                      description="Learn about Christomex - our history, mission, and the people behind our work."
                      url={`${BASE_URL}/about-us`}
                      image={OG_IMAGE}
                    />
                    <AboutUs />
                  </>
                }
              />
              <Route
                path="/christomex-way"
                element={
                  <>
                    <DocumentMeta
                      title="The Christomex Way"
                      description="Discover the principles and approach that define how Christomex operates."
                      url={`${BASE_URL}/christomex-way`}
                      image={OG_IMAGE}
                    />
                    <ChristomexWay />
                  </>
                }
              />
              <Route
                path="/products"
                element={
                  <>
                    <DocumentMeta
                      title="Products"
                      description="Explore Christomex's full range of products built for quality and reliability."
                      url={`${BASE_URL}/products`}
                      image={OG_IMAGE}
                    />
                    <Products />
                  </>
                }
              />
              <Route
                path="/careers"
                element={
                  <>
                    <DocumentMeta
                      title="Careers"
                      description="Join the Christomex team. Explore open roles and build your career with us."
                      url={`${BASE_URL}/careers`}
                      image={OG_IMAGE}
                    />
                    <Careers />
                  </>
                }
              />
              <Route
                path="/shared-values"
                element={
                  <>
                    <DocumentMeta
                      title="Shared Values"
                      description="The values that guide every decision at Christomex."
                      url={`${BASE_URL}/shared-values`}
                      image={OG_IMAGE}
                    />
                    <SharedValues />
                  </>
                }
              />
              <Route
                path="/sustainability"
                element={
                  <>
                    <DocumentMeta
                      title="Sustainability"
                      description="Christomex's commitment to sustainable practices and a better future."
                      url={`${BASE_URL}/sustainability`}
                      image={OG_IMAGE}
                    />
                    <Sustainability />
                  </>
                }
              />
              <Route
                path="/contact"
                element={
                  <>
                    <DocumentMeta
                      title="Contact Us"
                      description="Get in touch with the Christomex team. We'd love to hear from you."
                      url={`${BASE_URL}/contact`}
                      image={OG_IMAGE}
                    />
                    <ContactUs />
                  </>
                }
              />
              <Route
                path="*"
                element={
                  <>
                    <DocumentMeta
                      title="Page Not Found"
                      description="The page you're looking for doesn't exist. Head back to Christomex."
                      url={BASE_URL}
                      image={OG_IMAGE}
                      noIndex={true}
                    />
                    <NotFoundPage />
                  </>
                }
              />
            </Routes>
          </Suspense>
        </div>
      </Router>
    </HelmetProvider>
  );
}

export default App;